import { License } from '@app/license-management/types/license'
import { Organization } from '@app/organization-management/types/organization'
import { AccessRole } from './access-role'
import { ProfessionalRole } from './professional-role'
import { UserStatus } from './user-status'
import { ISODateString } from '@app/common/types/iso-date-string'
import { LegacyLicense } from './legacy-license'
import { LicensePool } from '@app/license-management/types/license-pool'
import { getCustomerFacingLicenseName } from '@app/common/util/get-customer-facing-license-name'
import { getLicensePoolTitle } from '@app/common/util/get-license-pool-title'
import { OrderType } from '@app/organization-management/types/order-type'
import { BillingPlatform } from '@app/organization-management/types/billing-platform'
import { PilotMetadata } from './pilot-metadata'

/** Archived by deletedAt (APT-1900); falls back to status where PAS doesn't send isArchived yet. */
export function isUserArchived(user: Pick<User, 'isArchived' | 'status'>): boolean {
  return user.isArchived ?? user.status === UserStatus.ARCHIVED
}

export class User {
  userId!: string
  email!: string
  firstName!: string
  lastName!: string
  accessRole!: AccessRole
  professionalRole!: ProfessionalRole | null
  belongsToActivePilot!: boolean
  pilot?: boolean
  pilotMetaData?: PilotMetadata | null
  daysActive!: number
  lastActivityAt?: string | null
  activeDaysLog?: string[] | undefined
  license!: License
  status!: UserStatus
  statusUpdatedAt?: ISODateString | null
  organization?: Organization
  legacyLicense!: LegacyLicense | null
  licensePool!: LicensePool | null
  /** Always populated by the backend (fromUserReadModel/Simplified) — never optional in practice. */
  hasLicense!: boolean
  /** APT-1900: deletedAt is set. Absent from older PAS responses; see isUserArchived. */
  isArchived?: boolean
  orderType?: OrderType

  licenseTypeDisplay(): string {
    if (this.hasLicense === false) {
      return 'No License'
    }

    if (this.organization?.billingPlatform === BillingPlatform.MAXIO) {
      if (!this.licensePool) {
        return 'No License'
      }
      return this.licensePoolDisplay(this.licensePool)
    }

    if (this.legacyLicense?.subscription?.plan) {
      return (
        getCustomerFacingLicenseName({
          unlimitedLicenses: this.legacyLicense.subscription.plan.unlimitedLicenses,
        }) ?? 'No License'
      )
    }

    if (this.licensePool) {
      return this.licensePoolDisplay(this.licensePool)
    }

    return 'No License'
  }

  private licensePoolDisplay(pool: LicensePool): string {
    return getCustomerFacingLicenseName({ licensePool: pool }) ?? getLicensePoolTitle(pool)
  }
}
